import { Puzzle, Repeat, ShoppingBag, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { StorefrontAddOn, StorefrontPlan } from '@/types/storefront'
import type { PublicProduct, PublicSubscriptionGroup } from '@/types/checkout'

/** What a store card sells: decides the eyebrow, the price line and the action. */
export type StoreItemKind = 'product' | 'plan' | 'addOn'

/** `grid`: one cell of the grid. `wide`: the featured item at the top, or the only one. */
export type StoreCardLayout = 'grid' | 'wide'

export type PlanOption = StorefrontPlan['options'][number]

/** The grid for `count` cards: it always fills the width, so a short store never leaves a hole. */
export const storeGridClass = (count: number) =>
  cn(
    'grid grid-cols-1 gap-6',
    count === 2 && 'sm:grid-cols-2',
    count === 3 && 'sm:grid-cols-2 lg:grid-cols-3',
    count >= 4 && 'sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4'
  )

/** The interval of a plan option, as the toggle and the price line name it. */
export const intervalLabelKey = (interval?: string | null) =>
  interval === 'YEARLY' ? 'checkout:perYear' : interval === 'MONTHLY' ? 'checkout:perMonth' : null

/**
 * The option a plan card prices: the one billed at `interval` (the toggle),
 * else its cheapest. The plan's page still lets the buyer pick another.
 */
export const planOptionFor = (plan: StorefrontPlan, interval: string | null): PlanOption | null => {
  const match = interval ? plan.options.filter((o) => o.billingInterval === interval) : []
  const pool = match.length > 0 ? match : plan.options
  return pool.reduce<PlanOption | null>((best, o) => (!best || Number(o.price) < Number(best.price) ? o : best), null)
}

/** A plan is "recommended" when the community featured one of its billing options. */
export const planIsRecommended = (plan: StorefrontPlan) =>
  plan.options.some((o) => Boolean(o.isHighlighted))

/**
 * One icon per kind, the same in the section heading, the card and the hero:
 * a plan repeats (a subscription), an add-on plugs into a plan, a product is
 * a bag (bought once).
 */
export const KIND_ICON: Record<StoreItemKind, LucideIcon> = { plan: Repeat, addOn: Puzzle, product: ShoppingBag }

/** A plan's description as benefit lines, one per line ("- " and bullets dropped). The API has no benefits field. */
export const planBenefitLines = (plan: StorefrontPlan): string[] =>
  (plan.description ?? '').split('\n').map((line) => line.replace(/^[-•*]\s*/, '').trim()).filter(Boolean)

/** A plan option that sells this add-on, with the add-on's price on its rhythm (F-37). */
export const optionsWithAddOn = (group: PublicSubscriptionGroup, addOnId: string): PublicProduct[] =>
  group.products.filter((option) => option.addOnAmounts?.some((amount) => amount.productId === addOnId))

/**
 * The plans that can carry this add-on in one checkout: those selling it with
 * the plan (`sellsWithPlan`) and with an option that prices it. In the
 * plans' own order.
 */
export const comboPlansFor = (addOn: StorefrontAddOn, groups: PublicSubscriptionGroup[]): PublicSubscriptionGroup[] =>
  groups.filter(
    (group) =>
      group.slug &&
      group.addOns?.some((candidate) => candidate.productId === addOn.id && candidate.sellsWithPlan) &&
      optionsWithAddOn(group, addOn.id).length > 0
  )

/** The option an offer card prices: the one billed at `interval`, else the monthly one, else the first. */
export const offerOptionAt = (options: PublicProduct[], interval: string | null): PublicProduct | null =>
  (interval && options.find((option) => option.billingInterval === interval)) ||
  options.find((option) => option.billingInterval === 'MONTHLY') ||
  options[0] ||
  null

/** What an offer card charges each period: the plan option plus the add-on it needs, if any. */
export const offerTotal = (options: PublicProduct[], addOnId: string | null, interval: string | null): number => {
  const option = offerOptionAt(options, interval)
  if (!option) return Number.POSITIVE_INFINITY
  const addOn = addOnId ? Number(option.addOnAmounts?.find((amount) => amount.productId === addOnId)?.amount ?? 0) : 0
  return Number(option.price) + addOn
}
